import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { useLang, canSeePrices } from "@/contexts/LanguageContext";
import api, { formatApiError, resolveImageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Trash2, Lock } from "lucide-react";

export default function Cart() {
  const { items, updateQty, remove, total, clear } = useCart();
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const showPrices = canSeePrices(user);
  const canOrder = showPrices;

  const submitOrder = async () => {
    if (!user || user === false) {
      navigate("/login?next=/cart");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        shipping_address: address,
        notes: notes || null,
      };
      await api.post("/orders", payload);
      clear();
      toast.success("✓");
      navigate("/dashboard");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div data-testid="cart-empty" className="max-w-3xl mx-auto px-6 py-20 lg:py-28 min-h-[55vh] flex flex-col items-center justify-center text-center">
        <div className="overline">{t("nav.cart")}</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter mt-3">{t("cart.empty")}</h1>
        <Link to="/catalog" className="mt-8 inline-block">
          <Button className="rounded-md h-11 px-6">{t("home.cta.browse")}</Button>
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 lg:py-16 grid lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2">
        <div className="overline mb-3">{t("nav.cart")}</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter mb-8">{t("cart.title")}</h1>
        <div className="border border-border bg-white">
          {items.map((i, idx) => (
            <div
              key={i.product_id}
              data-testid={`cart-item-${i.slug}`}
              className={`p-4 sm:p-5 flex flex-wrap sm:flex-nowrap gap-3 sm:gap-4 items-center ${idx > 0 ? "border-t border-border" : ""}`}
            >
              <img src={resolveImageUrl(i.image_url)} alt="" className="w-16 h-16 sm:w-20 sm:h-20 object-cover bg-secondary shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-sm sm:text-base truncate">{i.name}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {i.presentation} · {i.dose}
                </div>
                {showPrices ? (
                  <div className="font-mono text-sm mt-2">${i.price.toFixed(2)}</div>
                ) : (
                  <div className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-2">
                    <Lock className="w-3 h-3" /> {t("cart.priceLocked")}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 ml-auto sm:ml-0">
                <div className="flex items-center border border-border">
                  <button
                    className="w-8 h-9 hover:bg-secondary"
                    onClick={() => updateQty(i.product_id, Math.max(0, i.quantity - 1))}
                    data-testid={`cart-dec-${i.slug}`}
                  >−</button>
                  <span className="w-8 text-center font-mono text-sm">{i.quantity}</span>
                  <button
                    className="w-8 h-9 hover:bg-secondary"
                    onClick={() => updateQty(i.product_id, Math.min(20, i.quantity + 1))}
                    data-testid={`cart-inc-${i.slug}`}
                  >+</button>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => remove(i.product_id)}
                  data-testid={`cart-remove-${i.slug}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <aside className="lg:col-span-1">
        <div className="border border-border bg-white p-6 sticky top-24">
          <div className="overline mb-3">{t("cart.summary")}</div>

          {showPrices ? (
            <>
              <div className="flex items-center justify-between text-sm">
                <span>{t("cart.subtotal")}</span>
                <span className="font-mono">${total.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-2 text-muted-foreground">
                <span>{t("cart.shipping")}</span>
                <span className="font-mono">{t("cart.shippingNote")}</span>
              </div>
              <div className="thin-rule my-4" />
              <div className="flex items-center justify-between">
                <span className="font-display font-semibold">{t("cart.total")}</span>
                <span className="font-display font-bold text-xl" data-testid="cart-total">${total.toFixed(2)}</span>
              </div>
            </>
          ) : (
            <div className="border border-border bg-secondary/40 p-4 text-xs text-muted-foreground flex items-center gap-2">
              <Lock className="w-4 h-4" /> {t("cart.priceLocked")}
            </div>
          )}

          <div className="mt-6 space-y-3">
            <div>
              <Label htmlFor="addr" className="text-xs">{t("cart.address")}</Label>
              <Textarea
                id="addr"
                rows={3}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="mt-2"
                data-testid="cart-address"
                required
              />
            </div>
            <div>
              <Label htmlFor="notes" className="text-xs">{t("cart.notes")}</Label>
              <Input id="notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                     className="mt-2" data-testid="cart-notes" />
            </div>
          </div>

          <CheckoutAction
            user={user}
            canOrder={canOrder}
            navigate={navigate}
            submitting={submitting}
            address={address}
            submitOrder={submitOrder}
            t={t}
          />

          {error && <div className="mt-3 text-sm text-destructive" data-testid="cart-error">{error}</div>}
        </div>
      </aside>
    </div>
  );
}

function CheckoutAction({ user, canOrder, navigate, submitting, address, submitOrder, t }) {
  if (!user || user === false) {
    return (
      <Button
        className="w-full mt-6 h-11 rounded-md"
        onClick={() => navigate("/login?next=/cart")}
        data-testid="cart-signin"
      >
        {t("cart.signin")}
      </Button>
    );
  }
  if (!canOrder) {
    return (
      <div className="mt-6 text-xs text-muted-foreground border border-[hsl(var(--accent))]/30 bg-[hsl(var(--accent))]/5 p-3">
        {t("cart.gate")}
        <Button
          className="w-full mt-3 rounded-md"
          size="sm"
          onClick={() => navigate("/dashboard/verification")}
        >
          {t("cart.gate.btn")}
        </Button>
      </div>
    );
  }
  return (
    <Button
      className="w-full mt-6 h-11 rounded-md"
      onClick={submitOrder}
      disabled={submitting || !address}
      data-testid="cart-place-order"
    >
      {submitting ? t("cart.placing") : t("cart.place")}
    </Button>
  );
}
